// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

// TMI - content type and query definitions
import { SearchableTicker } from "@tmi/types/entities";
import LocalForagePlugin, { tickers } from "@/plugins/LocalForagePlugin";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// State values
const initialized = computed< boolean >( () => allTickers.value.length > 0 );
const loading = ref< boolean >( false );

const defaultTickers: SearchableTicker[] = [];
const allTickers = ref< SearchableTicker[] >( defaultTickers );
const searchableEtfs = ref< SearchableTicker[] >( defaultTickers );
const searchableStocks = ref< SearchableTicker[] >( defaultTickers );

export const useTickersStore = defineStore( 'tickers', () => {
    function reset(): void {
        allTickers.value.splice( 0 );
        searchableEtfs.value.splice( 0 );
        searchableStocks.value.splice( 0 );
        loading.value = false;
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Tickers ].initialize() exiting - already processing...' );
            return;
        }

        try {
            logger.debug( `[ Tickers ] Checking the local database for any tickers...` );

            loading.value = true;

            const t0: number = performance.now();

            // Wait for the driver to settle — unconditionally. The bindings are assigned synchronously,
            // but an instance's own ready() resolves before sibling stores finish upgrading the shared
            // database on a fresh origin; reading in that window hangs localforage (fresh-origin boot race).
            await LocalForagePlugin.initialize();

            await tickers.ready();
            const numberTickers = await tickers.length();

            const uniqueEtfs: Set< SearchableTicker > = new Set< SearchableTicker >();
            const uniqueStocks: Set< SearchableTicker > = new Set< SearchableTicker >();
            if ( numberTickers > 0 ) {
                // Clear the contents of the searchable collections
                allTickers.value.splice( 0 );
                searchableEtfs.value.splice( 0 );
                searchableStocks.value.splice( 0 );

                // Iterate over all elements within IDB
                //
                // Fix round 2 (Finding, C5 re-review): this used to be a fire-and-forget
                // `.then(...)` chain — `initialize()` (an async function) returned as soon as
                // `iterate()` was CALLED, not once it (and the population that depends on it)
                // actually finished. Every `await tickersStore.initialize()` caller — including
                // Application.ts's worker-message empty -> populated transition check for C5's
                // re-hydration trigger — resolved before `allTickers` was ever assigned, so
                // `allTickers.value.length` read 0 regardless of what IDB actually held. `await`
                // it properly instead, matching the pattern `TickerCrosswalk.ts` already uses.
                await tickers.iterate( ( ticker: SearchableTicker ) => {
                    ticker.isEtf ? uniqueEtfs.add( ticker ) : uniqueStocks.add( ticker );
                    return;
                } );

                searchableStocks.value = [ ...uniqueStocks ];
                searchableEtfs.value = [ ...uniqueEtfs ];
                allTickers.value = [ ...uniqueStocks, ...uniqueEtfs ];

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ searchableStocks.value.length } stock & ${ searchableEtfs.value.length } etf tickers in ${ t1 - t0 } ms` );
            }
        } catch( err: any ) {
            logger.error( JSON.stringify( err.message ) )
            reset();
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    function tickerById( id: string ): SearchableTicker | undefined {
        return allTickers.value.find( ( ticker: SearchableTicker ): boolean => ticker.id === id );
    }

    // C5 (Option 1): the symbol-side half of the content-id -> symbol -> ticker crosswalk lookup
    // used by content-hydration.ts. Exact match against `ticker` — the sentient catalog's symbols
    // are already normalized, same as the pre-existing `tickerById` lookup above.
    function tickerBySymbol( symbol: string ): SearchableTicker | undefined {
        return allTickers.value.find( ( ticker: SearchableTicker ): boolean => ticker.ticker === symbol );
    }

    return {
        // ACTIONS
        initialize,
        reset,
        tickerById,
        tickerBySymbol,

        // GETTERS
        allTickers,
        searchableEtfs,
        searchableStocks
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useTickersStore, import.meta.hot ) );
}
